import { IPredictionGroupingDto } from 'api/api';
import moment from 'moment';
import React, { FC, memo, useMemo } from 'react';
import { localized } from 'state/i18n';
import BiLabelContentItem from 'view/shared/components/bi-label-content/bi-label-content-item';

interface Props {
	label: string;
	data: IPredictionGroupingDto[];
	daysAway?: number; // If undefined, calculates overall average
}

export const PredictionSummaryItem: FC<Props> = memo(({ label, data, daysAway }) => {
	const value = useMemo(() => {
		if (!data || data.length === 0) return NaN;

		// Calculate for a specific target day (e.g., 7 days away or 3 days away)
		if (daysAway !== undefined) {
			const validGroupings = data
				.map(pg => {
					const prediction = pg.predictions?.find(
						p => Math.abs(moment(pg.actualEmptiedDate).diff(p.predictionDate, 'days')) === daysAway
					);
					return { pg, prediction };
				})
				.filter(item => item.prediction !== undefined);

			if (validGroupings.length === 0) return NaN;

			const totalDiffMinutes = validGroupings.reduce((sum, { pg, prediction }) => {
				const diff = Math.abs(
					prediction!.predictedMinutesUntilFull! -
						moment(pg.actualEmptiedDate).diff(prediction!.predictionDate, 'minutes')
				);
				return sum + diff;
			}, 0);

			return totalDiffMinutes / validGroupings.length / (60 * 24);
		}

		// Calculate the Overall Average
		const overallDiff = data.reduce(
			(acc, curr) => {
				const diffSum =
					curr.predictions?.reduce((sum, pred) => {
						const diff = Math.abs(
							pred.predictedMinutesUntilFull! -
								moment(curr.actualEmptiedDate).diff(pred.predictionDate, 'minutes')
						);
						return sum + diff;
					}, 0) ?? 0;

				return {
					totalDiff: acc.totalDiff + diffSum,
					count: acc.count + (curr.predictions?.length ?? 0),
				};
			},
			{ totalDiff: 0, count: 0 }
		);

		return overallDiff.count > 0 ? overallDiff.totalDiff / (60 * 24) / overallDiff.count : NaN;
	}, [data, daysAway]);

	return (
		<BiLabelContentItem
			defaultClassNames="bi-label-content-item-lg unit-manufacturerer-grid"
			containerClassName="margin-bottom-10px"
			label={label}
			content={Number.isFinite(value) ? `${value.toFixed(2)} ${localized('days')}` : 'N/A'}
		/>
	);
});
